import { useCallback, useState } from 'react'
import * as React from 'react'
import { defineMessages, useIntl } from 'react-intl'
import type { CSSProperties } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'

import { toBaseFareClass } from 'lib/flights/rewardFareClasses'
import { isRewardFareClass, type FareClassWithReward } from 'constants/flight'
import MaldivesCharterTableExpandedFareFeatureCell from './MaldivesCharterTableExpandedFareFeatureCell'
import MaldivesCharterTableExpandedSocieteCell from './MaldivesCharterTableExpandedSocieteCell'
import {
  ExpandedSectionFareTd,
  ExpandedSectionStickyTd,
} from './MaldivesCharterTripOptionsTable'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

const PendingCellPad = styled.div`
  padding: ${rem(12)};
`

/** Zero-height marker row between Baggage and Seats. The parent table attaches a ref callback to
 *  the inner span and measures its Y position so it can render the second pair of horizontal
 *  scroll arrows in {@link TableWrap} (outside the horizontal scroll track) — same overhang
 *  treatment as the column-header arrow pair. */
const ExpandedSectionAnchorTd = styled.td`
  padding: 0;
  border: none;
  line-height: 0;
  font-size: 0;
  height: 0;
`

const messages = defineMessages({
  baggageSection: {
    id: 'zcz2kGGq5yw_5xp_6UiB',
    defaultMessage: 'Baggage',
    description: 'Maldives charter desktop trip-options table expanded row subsection heading above the per-cabin baggage allowance list.',
  },
  seatsMealsSection: {
    id: 'sn72ng9h0P2OZz-elD-h',
    defaultMessage: 'Seats, meals and more',
    description: 'Maldives charter desktop trip-options table expanded row subsection heading above the per-cabin seats / meals / extras list.',
  },
  societeBenefitsSection: {
    id: 'yobG5UuhkJTkVWyNt84v',
    defaultMessage: '{loyaltyProgramName} benefits',
    description: 'Maldives charter desktop trip-options table expanded row subsection heading above the per-cabin loyalty (Société) earn breakdown.',
  },
  baggagePending: {
    id: 's9AnH85zaJ__qhuLZAbN',
    defaultMessage: 'Baggage restrictions will appear once matching flights are available for this stay.',
    description: 'Placeholder in the Maldives charter expanded row baggage cell when reward seats are unavailable.',
  },
  fareInclusionsPending: {
    id: 'KL-r9eicrEsHYTPPtBHg',
    defaultMessage: 'Fare inclusions will appear once matching flights are available for this stay.',
    description: 'Placeholder in the Maldives charter expanded row seats/meals cell when reward seats are unavailable.',
  },
  societeBenefitsPending: {
    id: 'yYGck1I211h4TE7HYoVJ',
    defaultMessage: '{loyaltyProgramName} benefits will appear once matching flights are available for this cabin.',
    description: 'Placeholder in the Maldives charter expanded row Société cell when reward seats are unavailable.',
  },
})

interface Props {
  fareClassColumns: ReadonlyArray<FareClassWithReward>
  fareColumnCellStyle: CSSProperties
  checkIn: string
  checkOut: string
  rooms: Array<App.Occupants>
  /** Pre-built points-earn calculation request for the hotel portion of the bundle, sourced from
   *  the row's `bundleData` (built in `useCalendarItemData`). Wired into the Société cell so it
   *  can render hotel earn alongside flight earn for cash cabins, and as the only earn line for
   *  reward cabins (where the flight is paid in points and earns no loyalty). */
  hotelLoyaltyCalculationRequest?: App.LoyaltyPointsEarnCalculationRequest
  /** Callback ref attached to a zero-height marker between the Baggage and Seats rows so the
   *  parent table can measure the marker's Y position and render the second pair of horizontal
   *  scroll arrows in `TableWrap` (outside the scroll track) at that same vertical level. */
  expandedAnchorRef?: React.RefCallback<HTMLSpanElement>
  /** Set of fare class columns that are unavailable (no seats / not logged in for reward). */
  unavailableColumns: Set<FareClassWithReward>
}

/**
 * Baggage + Seats/meals comparison rows that render directly under an expanded date row in
 * {@link MaldivesCharterTripOptionsTable}. Owns its own `extraFeaturesExpanded` state so the
 * "Show more" toggle on the seats/meals section persists for as long as the date row is open.
 *
 * Returns two sibling `<tr>` elements via a fragment so the rows slot directly into the table's
 * `<tbody>` and inherit the card-border/sticky-column treatment from the surrounding cells.
 */
function MaldivesCharterTripOptionsTableExpandedRow({
  fareClassColumns,
  fareColumnCellStyle,
  checkIn,
  checkOut,
  rooms,
  hotelLoyaltyCalculationRequest,
  expandedAnchorRef,
  unavailableColumns,
}: Props) {
  const intl = useIntl()
  const [extraFeaturesExpanded, setExtraFeaturesExpanded] = useState(false)
  const toggleExtraFeaturesExpanded = useCallback(() => setExtraFeaturesExpanded(v => !v), [])
  const loyaltyProgramName = getLoyaltyProgramDisplayName()

  const baggageSectionLabel = intl.formatMessage(messages.baggageSection)
  const seatsMealsSectionLabel = intl.formatMessage(messages.seatsMealsSection)
  const societeBenefitsSectionLabel = intl.formatMessage(
    messages.societeBenefitsSection,
    { loyaltyProgramName },
  )
  const baggagePendingLabel = intl.formatMessage(messages.baggagePending)
  const fareInclusionsPendingLabel = intl.formatMessage(messages.fareInclusionsPending)
  const societeBenefitsPendingLabel = intl.formatMessage(messages.societeBenefitsPending, { loyaltyProgramName })
  const lastFareColumnIndex = fareClassColumns.length - 1

  return <>
    <tr>
      <ExpandedSectionStickyTd scope="row" $isLastExpandedRow={false}>
        <BodyText variant="small" weight="bold">{baggageSectionLabel}</BodyText>
      </ExpandedSectionStickyTd>
      {fareClassColumns.map((fareClassColumn, idx) => {
        const unavailable = unavailableColumns.has(fareClassColumn)
        return (
          <ExpandedSectionFareTd
            key={fareClassColumn}
            style={fareColumnCellStyle}
            $isLastFareColumn={idx === lastFareColumnIndex}
            $isLastExpandedRow={false}
          >
            {unavailable && <PendingCellPad><BodyText variant="small" colour="neutral-three">{baggagePendingLabel}</BodyText></PendingCellPad>}
            {!unavailable && <MaldivesCharterTableExpandedFareFeatureCell
              fareClass={toBaseFareClass(fareClassColumn)}
              checkIn={checkIn}
              checkOut={checkOut}
              rooms={rooms}
              section="baggage"
            />}
          </ExpandedSectionFareTd>
        )
      })}
    </tr>
    {expandedAnchorRef && (
      <tr aria-hidden>
        <ExpandedSectionAnchorTd colSpan={fareClassColumns.length + 1}>
          <span ref={expandedAnchorRef} />
        </ExpandedSectionAnchorTd>
      </tr>
    )}
    <tr>
      <ExpandedSectionStickyTd scope="row" $isLastExpandedRow={false}>
        <BodyText variant="small" weight="bold">{seatsMealsSectionLabel}</BodyText>
      </ExpandedSectionStickyTd>
      {fareClassColumns.map((fareClassColumn, idx) => {
        const unavailable = unavailableColumns.has(fareClassColumn)
        return (
          <ExpandedSectionFareTd
            key={fareClassColumn}
            style={fareColumnCellStyle}
            $isLastFareColumn={idx === lastFareColumnIndex}
            $isLastExpandedRow={false}
          >
            {unavailable && <PendingCellPad><BodyText variant="small" colour="neutral-three">{fareInclusionsPendingLabel}</BodyText></PendingCellPad>}
            {!unavailable && <MaldivesCharterTableExpandedFareFeatureCell
              fareClass={toBaseFareClass(fareClassColumn)}
              checkIn={checkIn}
              checkOut={checkOut}
              rooms={rooms}
              section="extra"
              extraFeaturesExpanded={extraFeaturesExpanded}
              onToggleExtraFeatures={toggleExtraFeaturesExpanded}
            />}
          </ExpandedSectionFareTd>
        )
      })}
    </tr>
    <tr>
      <ExpandedSectionStickyTd scope="row" $isLastExpandedRow>
        <BodyText variant="small" weight="bold">{societeBenefitsSectionLabel}</BodyText>
      </ExpandedSectionStickyTd>
      {fareClassColumns.map((fareClassColumn, idx) => {
        const unavailable = unavailableColumns.has(fareClassColumn)
        return (
          <ExpandedSectionFareTd
            key={fareClassColumn}
            style={fareColumnCellStyle}
            $isLastFareColumn={idx === lastFareColumnIndex}
            $isLastExpandedRow
          >
            {unavailable && <PendingCellPad><BodyText variant="small" colour="neutral-three">{societeBenefitsPendingLabel}</BodyText></PendingCellPad>}
            {!unavailable && <MaldivesCharterTableExpandedSocieteCell
              fareClass={toBaseFareClass(fareClassColumn)}
              checkIn={checkIn}
              checkOut={checkOut}
              rooms={rooms}
              isReward={isRewardFareClass(fareClassColumn)}
              hotelLoyaltyCalculationRequest={hotelLoyaltyCalculationRequest}
            />}
          </ExpandedSectionFareTd>
        )
      })}
    </tr>
  </>
}

export default MaldivesCharterTripOptionsTableExpandedRow
